import React, { Fragment, useCallback, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Flex, Box, NakedButton, Heading, Button, Text } from 'roo-ui/components';
import { useSelector, useDispatch } from 'react-redux';
import Decimal from 'decimal.js';
import FullPointsPaySlider from './FullPointsPaySlider';
import Currency from 'components/Currency';
import { getPointsErrors } from 'store/booking/bookingSelectors';
import {
  getPayableNowCashAmount,
  getTotalPayableAfterCredits,
  getPointsAmount,
  getPointsAmountInCash,
} from 'store/checkout/checkoutSelectors';
import { getIsAuthenticated, getPointsBalance } from 'store/user/userSelectors';
import { updatePayments } from 'store/checkout/checkoutActions';
import { getQffAuth } from 'lib/qffAuth';
import { useDataLayer } from 'hooks/useDataLayer';
import usePointsRedemptionAvailable from 'pages/Checkout/CustomerDetails/Steps/PaymentOptions/hooks/usePointsRedemptionAvailable';
import PointsLearnMore from 'pages/Checkout/CustomerDetails/Steps/PaymentOptions/PointsLearnMore';
import Modal from 'components/Modal';
import BookingError from 'pages/Checkout/BookingError';
import { useModal } from 'lib/hooks';
import { MIN_POINTS_AMOUNT } from 'config';
import usePointsConverters from 'hooks/points/usePointsConverters';
import {
  PaymentMethodSummaryContainer,
  PaymentMethodSummaryButton,
  MinimumPointsMessages,
} from 'pages/Checkout/CustomerDetails/Steps/PaymentOptions/PaymentMethodSummary';
import formatNumber from 'lib/formatters/formatNumber';
import TreatmentToggle from 'components/TreatmentToggle';
import TravelPass from 'pages/Checkout/CustomerDetails/Steps/PaymentOptions/TravelPass';
import HotelsVoucher from 'pages/Checkout/CustomerDetails/Steps/PaymentOptions/HotelsVoucher';

const login = async () => {
  try {
    const qffAuth = await getQffAuth();
    qffAuth.showLoginModal();
  } catch (e) {
    //do nothing yet
  }
};

const ModalFooter = ({ applyPointsAndCashFromModal, clearPoints }) => {
  return (
    <Fragment>
      <Box borderTop={1} borderColor="greys.alto" mt={'auto'}>
        <Flex px={6} py={4} justifyContent="space-between">
          <NakedButton onClick={clearPoints} color="brand.primary" data-testid="do-not-use-points-button">
            <Text fontWeight="bold" letterSpacing="1px">
              CLEAR POINTS
            </Text>
          </NakedButton>
          <Button onClick={applyPointsAndCashFromModal} data-testid="apply-points-and-cash-button" variant="primary">
            Confirm
          </Button>
        </Flex>
      </Box>
    </Fragment>
  );
};

ModalFooter.propTypes = {
  applyPointsAndCashFromModal: PropTypes.func.isRequired,
  clearPoints: PropTypes.func.isRequired,
};

const FullPointsPay = () => {
  const dispatch = useDispatch();
  const { openModal: openPointsPayModal, closeModal: closePointsPayModal, modalProps: pointsPayModalProps } = useModal();
  const isAuthenticated = useSelector(getIsAuthenticated);
  const pointsBalance = useSelector(getPointsBalance);
  const totalPayableAfterCredits = useSelector(getTotalPayableAfterCredits);
  const pointsAmount = useSelector(getPointsAmount);
  const pointsAmountInCash = useSelector(getPointsAmountInCash);
  const payableNowCashAmount = useSelector(getPayableNowCashAmount);
  const pointsErrors = useSelector(getPointsErrors);
  const hasPointsAmountGreaterThanZero = pointsAmount > 0;
  const [bufferedPointsAndCash, setBufferedPointsAndCash] = useState();
  const { emitInteractionEvent } = useDataLayer();
  const { convertPointsToCash } = usePointsConverters();
  const { isPointsPayAvailable } = usePointsRedemptionAvailable();

  const defaultPointsAndCash = useMemo(() => {
    return {
      cash: {
        amount: payableNowCashAmount,
      },
      points: {
        amount: pointsAmount,
        amountInCash: pointsAmountInCash,
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payableNowCashAmount.toNumber(), pointsAmount.toNumber(), pointsAmountInCash.toNumber()]);

  useEffect(() => {
    setBufferedPointsAndCash(defaultPointsAndCash);
  }, [defaultPointsAndCash]);

  const applyPointsAndCashFromModal = useCallback(() => {
    dispatch(
      updatePayments({
        cash: {
          payableNow: {
            amount: bufferedPointsAndCash.cash.amount,
          },
          payableLater: {
            amount: new Decimal(0),
          },
        },
        points: {
          amount: bufferedPointsAndCash.points.amount,
          amountInCash: bufferedPointsAndCash.points.amountInCash,
        },
      }),
    );

    closePointsPayModal();

    emitInteractionEvent({
      type: 'Use Points and Pay Pop Up',
      value: 'Use Points and Pay Apply Button Selected',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bufferedPointsAndCash, closePointsPayModal]);

  const editPointsPay = useCallback(() => {
    if (pointsAmount.isZero()) {
      const minPointsAmountInCash = convertPointsToCash({ points: MIN_POINTS_AMOUNT });

      setBufferedPointsAndCash({
        cash: {
          amount: totalPayableAfterCredits.minus(minPointsAmountInCash),
        },
        points: {
          amount: MIN_POINTS_AMOUNT,
          amountInCash: minPointsAmountInCash,
        },
      });
    }

    openPointsPayModal();

    emitInteractionEvent({
      type: 'Use Points and Pay Pop Up',
      value: 'Use Points and Pay Opened',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convertPointsToCash, emitInteractionEvent, openPointsPayModal, pointsAmount.toNumber(), totalPayableAfterCredits.toNumber()]);

  const closePointsPay = useCallback(() => {
    closePointsPayModal();
    setBufferedPointsAndCash(defaultPointsAndCash);

    emitInteractionEvent({
      type: 'Use Points and Pay Pop Up',
      value: 'Use Points and Pay Closed',
    });
  }, [closePointsPayModal, defaultPointsAndCash, emitInteractionEvent]);

  const clearPoints = useCallback(() => {
    dispatch(
      updatePayments({
        cash: {
          payableNow: {
            amount: totalPayableAfterCredits,
          },
          payableLater: {
            amount: new Decimal(0),
          },
        },
        points: {
          amount: new Decimal(0),
          amountInCash: new Decimal(0),
        },
      }),
    );

    closePointsPayModal();
    setBufferedPointsAndCash(defaultPointsAndCash);

    emitInteractionEvent({
      type: 'Use Points and Pay Pop Up',
      value: 'Use Points and Pay Clear Points Link Selected',
    });
  }, [closePointsPayModal, defaultPointsAndCash, dispatch, emitInteractionEvent, totalPayableAfterCredits]);

  return (
    <Fragment>
      <Box mb={10}>
        <Heading.h3 fontSize="md">Use credit or voucher.</Heading.h3>
        <TreatmentToggle split="qantas_travel_pass" treatment={['on']}>
          <TravelPass mb={4} />
        </TreatmentToggle>
        <HotelsVoucher mb={4} />
      </Box>
      <Heading.h3 fontSize="md" data-testid="redeem-message">
        Redeem Qantas Points on your hotel booking. <PointsLearnMore mb={4} />
      </Heading.h3>
      <BookingError errors={pointsErrors} type="Qantas Points" />
      <Fragment>
        <PaymentMethodSummaryContainer border={1} borderRadius={isAuthenticated ? 'exageratedRoundTopOnly' : 'exagerated'}>
          <Flex alignItems={['left', 'center']} justifyContent="space-between" width="100%" flexDirection={['column', 'row']}>
            <Text fontWeight="bold" fontSize={['sm', 'base']}>
              Qantas Points + Pay
            </Text>
            <Box mr={[0, 4]}>
              {totalPayableAfterCredits.greaterThan(0) && (
                <Fragment>
                  {isPointsPayAvailable && hasPointsAmountGreaterThanZero && (
                    <Fragment>
                      <Currency amount={pointsAmount} currency="PTS" fontSize={['xs', 'base']} data-testid="points-amount" />{' '}
                      <Text fontSize={['xs', 'base']}>+</Text>{' '}
                      <Currency amount={payableNowCashAmount} currency="AUD" fontSize={['xs', 'base']} data-testid="cash-amount" />
                    </Fragment>
                  )}
                  {isAuthenticated && <MinimumPointsMessages />}
                </Fragment>
              )}
            </Box>
          </Flex>
          {isAuthenticated && (
            <PaymentMethodSummaryButton
              onClick={editPointsPay}
              disabled={!isPointsPayAvailable}
              variant="primary"
              data-testid="edit-points-pay-button"
            >
              {hasPointsAmountGreaterThanZero ? 'CHANGE' : 'USE'}
            </PaymentMethodSummaryButton>
          )}
          {!isAuthenticated && (
            <PaymentMethodSummaryButton onClick={login} data-testid="login-button" variant="primary">
              LOGIN
            </PaymentMethodSummaryButton>
          )}
        </PaymentMethodSummaryContainer>

        {isAuthenticated && (
          <Box
            bg="greys.porcelain"
            px={4}
            py={2}
            border={1}
            borderColor="greys.alto"
            borderRadius="exageratedRoundBottomOnly"
            borderTop={0}
          >
            <Text fontSize="sm" data-testid="available-points">
              {formatNumber({ number: pointsBalance })} points available
            </Text>
          </Box>
        )}

        {pointsPayModalProps.isOpen && (
          <Modal
            {...pointsPayModalProps}
            onRequestClose={closePointsPay}
            title="Use Points + Pay"
            padding={0}
            footerComponent={() => <ModalFooter clearPoints={clearPoints} applyPointsAndCashFromModal={applyPointsAndCashFromModal} />}
          >
            <Box padding={[6, 8]}>
              <Box pt={6}>
                <FullPointsPaySlider
                  initialPointsAmount={bufferedPointsAndCash?.points?.amount}
                  totalCashAmount={totalPayableAfterCredits}
                  updatePointsAndCash={setBufferedPointsAndCash}
                />
              </Box>
              <Text mt={2} color="greys.steel" fontSize="xs" display="block">
                Minimum {formatNumber({ number: MIN_POINTS_AMOUNT })} pts is required
              </Text>
            </Box>
          </Modal>
        )}
      </Fragment>
    </Fragment>
  );
};

export default FullPointsPay;
